import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import EditorInput, { EditorInputProps } from "../EditorInput";
import Chevron from "../../../../assets/icons/Chevron";
import { MdSearch, MdFilterList, MdClear, MdCheck, MdWarning, MdAdd, MdClose, MdUnfoldMore, MdUnfoldLess, MdDownload } from "react-icons/md";
import useProgram from "../../../../utils/hooks/programs/useProgram";
import { useGetAssetTargetsQuery, useGetTargetResourcesQuery, useGetRunScopeOptionsQuery } from "../../../../utils/api/endpoints/programsApi";
import { createTargetResource, ResourceType } from "../../../../utils/api/endpoints/targetResource";
import toast from "react-hot-toast";

interface TargetEndpointsSectionProps {
    target: any;
    selectedAssetId: string;
    useSnapshot: boolean;
    snapshotOptions: any;
    programId: string;
    selectedTargetIds: string[];
    selectedEndpointIds: number[];
    toggleTarget: (id: string, name: string) => void;
    toggleEndpoint: (id: number, targetId: string) => void;
    onResourceFetched: (res: any) => void;
    isTargetSelected: boolean;
}

const TargetEndpointsSection: React.FC<TargetEndpointsSectionProps> = ({
    target,
    selectedAssetId,
    useSnapshot,
    snapshotOptions,
    programId,
    selectedTargetIds,
    selectedEndpointIds,
    toggleTarget,
    toggleEndpoint,
    onResourceFetched,
    isTargetSelected
}) => {
    const { setValue } = useFormContext();

    // Fetch live resources for this target - only called when mounted (expanded)
    const { data: liveTargetResources = [], isLoading: isLoadingLive, refetch } = useGetTargetResourcesQuery({
        assetId: selectedAssetId,
        targetId: target.target_id,
        includeDiscovered: true
    }, {
        skip: !selectedAssetId
    });

    // Local filters for this target's endpoints
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [isAdding, setIsAdding] = useState(false);

    // Endpoint Addition Form
    const [newVal, setNewVal] = useState("");
    const [newType, setNewType] = useState<ResourceType>("PATH");
    const [newMethod, setNewMethod] = useState("GET");
    const [newNote, setNewNote] = useState("Discovered during report creation");

    const resources = useMemo(() => {
        const snapshotResources = (useSnapshot && target.target_id)
            ? (snapshotOptions?.endpointsByTarget?.[target.target_id] || [])
            : [];
        
        if (!useSnapshot) return liveTargetResources;

        const seen = new Set(snapshotResources.map((r: any) => `${r.resource_type}:${r.value}`));
        const discoveries = liveTargetResources.filter((r: any) => !seen.has(`${r.resource_type}:${r.value}`));

        return [...snapshotResources, ...discoveries];
    }, [useSnapshot, target.target_id, snapshotOptions, liveTargetResources]);

    // Lift up resources to main component when they arrive
    useEffect(() => {
        resources.forEach(r => onResourceFetched(r));
    }, [resources, onResourceFetched]);

    const filtered = useMemo(() => {
        return resources.filter((r: any) => {
            const matchesSearch = !search || r.value.toLowerCase().includes(search.toLowerCase());
            const matchesType = typeFilter === "ALL" || r.resource_type === typeFilter;
            return matchesSearch && matchesType;
        });
    }, [resources, search, typeFilter]);

    const resourceTypes = useMemo(() => {
        return Array.from(new Set(resources.map((r: any) => r.resource_type))).sort();
    }, [resources]);

    const handleAdd = async () => {
        if (!newVal.trim()) return;
        if (!selectedAssetId) {
            toast.error("Cannot add endpoint: Asset ID is not loaded. Please select a program first.");
            return;
        }
        if (!programId) {
            toast.error("Cannot add endpoint: Program ID is not loaded. Please select a program first.");
            return;
        }
        if (!newNote.trim()) {
            toast.error("Discovery notes are required.");
            return;
        }

        const loader = toast.loading("Adding discovery...");
        try {
            const result = await createTargetResource({
                asset_id: String(selectedAssetId),
                target_id: target.target_id,
                value: newVal,
                resource_type: newType,
                http_method: newType === 'PATH' ? newMethod : undefined,
                status: "IN_SCOPE",
                notes: newNote.trim(),
                // Correcting potential type mismatch for program_id if it expects number
                program_id: programId ? parseInt(programId as any) : undefined 
            });
            toast.success("Added!");
            setIsAdding(false);
            setNewVal("");
            await refetch();
            toggleEndpoint(result.resource_id, target.target_id);
            onResourceFetched(result);
        } catch (e: any) {
            const serverMessage = e?.response?.data?.message || e?.message || "";
            toast.error(serverMessage ? `Failed to add resource: ${serverMessage}` : "Failed to add resource");
        } finally {
            toast.dismiss(loader);
        }
    };

    const selectFiltered = () => {
        const filteredIds = filtered.map((r: any) => r.resource_id);
        const allSelected = filteredIds.every(id => selectedEndpointIds.includes(id));
        
        let nextIds: number[];
        if (allSelected) {
            nextIds = selectedEndpointIds.filter(id => !filteredIds.includes(id));
        } else {
            nextIds = Array.from(new Set([...selectedEndpointIds, ...filteredIds]));
            // Auto-select target
            if (!isTargetSelected) toggleTarget(target.target_id, target.target_name);
        }
        setValue("endpointIds", nextIds);
    };

    return (
        <div className="border-t border-slate-100 bg-slate-50/20 p-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="flex items-center justify-between">
                <h5 className="text-[9px] font-black uppercase text-slate-400">Endpoints Selection</h5>
                <div className="flex items-center gap-2">
                    {resources.length > 0 && (
                        <button 
                            type="button" 
                            onClick={selectFiltered}
                            className="text-[9px] font-bold text-blue-600 hover:text-blue-700"
                        >
                            {(search || typeFilter !== "ALL") ? "Select Filtered" : "Select All"}
                        </button>
                    )}
                    <button 
                        type="button" 
                        onClick={() => setIsAdding(!isAdding)}
                        className="flex items-center gap-1 text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100"
                    >
                        {isAdding ? <MdClose /> : <MdAdd />} Discovery
                    </button>
                </div>
            </div>

            {isAdding ? (
                <div className="bg-white border border-emerald-100 p-2 rounded-lg shadow-sm space-y-2">
                    <div className="flex gap-2">
                        <select value={newType} onChange={(e) => setNewType(e.target.value as ResourceType)} className="text-[10px] bg-slate-50 border rounded p-1 outline-none">
                            <option value="PATH">PATH</option>
                            <option value="PARAMETER">PARAM</option>
                            <option value="HEADER">HEADER</option>
                            <option value="IP">IP</option>
                        </select>
                        <input autoFocus placeholder="Endpoint value..." value={newVal} onChange={(e) => setNewVal(e.target.value)} className="flex-1 text-[11px] border rounded px-2 outline-none" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[9px] font-black uppercase text-slate-400">Discovery Notes</label>
                        <textarea 
                            placeholder="Brief note about this discovery..." 
                            value={newNote} 
                            onChange={(e) => setNewNote(e.target.value)} 
                            className="w-full text-[11px] border rounded px-2 py-1 outline-none min-h-[40px] resize-none"
                        />
                    </div>
                    {newType === 'PATH' && (
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Method:</span>
                            {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => (
                                <button key={m} type="button" onClick={() => setNewMethod(m)} className={`px-1.5 py-0.5 rounded text-[9px] font-black ${newMethod === m ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{m}</button>
                            ))}
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setIsAdding(false)} className="text-[10px] text-slate-400">Cancel</button>
                        <button type="button" onClick={handleAdd} disabled={!newVal} className="text-[10px] font-bold bg-emerald-600 text-white px-3 py-1 rounded">Add</button>
                    </div>
                </div>
            ) : (
                <>
                    {resources.length > 0 && (
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <MdSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    placeholder="Search..." 
                                    value={search} 
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-7 pr-2 py-1 bg-white border rounded text-[10px] outline-none focus:border-blue-400"
                                />
                            </div>
                            {resourceTypes.length > 1 && (
                                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="text-[10px] bg-white border rounded px-1 outline-none">
                                    <option value="ALL">All</option>
                                    {resourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            )}
                        </div>
                    )}

                    {isLoadingLive ? (
                        <div className="text-center py-4 text-[10px] text-slate-400 italic">Loading...</div>
                    ) : resources.length === 0 ? (
                        <div className="text-center py-6 border border-dashed rounded bg-white">
                            <p className="text-[10px] text-slate-400 italic">No endpoints found.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                            {filtered.map((res: any) => (
                                <div 
                                    key={res.resource_id}
                                    onClick={() => toggleEndpoint(res.resource_id, target.target_id)}
                                    className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-all ${selectedEndpointIds.includes(res.resource_id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-100 hover:border-slate-200'}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[8px] font-black px-1 py-0.5 rounded bg-slate-100 text-slate-500">{res.resource_type}</span>
                                        <span className="text-[11px] text-slate-700 truncate max-w-[150px]">{res.value}</span>
                                    </div>
                                    <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${selectedEndpointIds.includes(res.resource_id) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                                        {selectedEndpointIds.includes(res.resource_id) && <MdCheck className="text-white text-[8px]" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

interface TargetItemProps {
    target: any;
    selectedAssetId: string;
    useSnapshot: boolean;
    snapshotOptions: any;
    programId: string;
    selectedTargetIds: string[];
    selectedEndpointIds: number[];
    toggleTarget: (id: string, name: string) => void;
    isExpanded: boolean;
    onToggleExpansion: () => void;
    toggleEndpoint: (id: number, targetId: string) => void;
    onResourceFetched: (res: any) => void;
    resourceCount: number;
}

const TargetItem: React.FC<TargetItemProps> = ({
    target,
    selectedAssetId,
    useSnapshot,
    snapshotOptions,
    programId,
    selectedTargetIds,
    selectedEndpointIds,
    toggleTarget,
    isExpanded,
    onToggleExpansion,
    toggleEndpoint,
    onResourceFetched,
    resourceCount
}) => {
    const isTargetSelected = selectedTargetIds.includes(target.target_id);

    return (
        <div className={`group border rounded-xl overflow-hidden transition-all ${isExpanded ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
            <div 
                className={`p-3 flex items-center justify-between cursor-pointer ${isTargetSelected ? 'bg-blue-50/50' : 'bg-white'}`}
                onClick={onToggleExpansion}
            >
                <div className="flex items-center gap-3 flex-1">
                    <div 
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isTargetSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}
                        onClick={(e) => { e.stopPropagation(); toggleTarget(target.target_id, target.target_name); }}
                    >
                        {isTargetSelected && <MdCheck className="text-white text-xs" />}
                    </div>
                    <div>
                        <p className="text-sm font-bold text-slate-900">{target.target_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-[10px] text-slate-500 font-medium uppercase">
                                {target.target_type}
                                {(!(target.target_value === "Uploaded Binary" || target.target_value === target.metadata?.binary_name) || !target.metadata?.binary_url) && ` • ${target.target_value}`}
                            </p>
                            {target.metadata?.binary_url && (
                                <a
                                    href={target.metadata.binary_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-[9px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                    title={target.metadata.binary_name || "Download File"}
                                >
                                    <MdDownload className="w-2.5 h-2.5" /> {target.metadata.binary_name || "File"}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!isExpanded && (
                        <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">
                            {resourceCount} Endpoints
                        </span>
                    )}
                    <Chevron className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {isExpanded && (
                <TargetEndpointsSection
                    target={target}
                    selectedAssetId={selectedAssetId}
                    useSnapshot={useSnapshot}
                    snapshotOptions={snapshotOptions}
                    programId={programId}
                    selectedTargetIds={selectedTargetIds}
                    selectedEndpointIds={selectedEndpointIds}
                    toggleTarget={toggleTarget}
                    toggleEndpoint={toggleEndpoint}
                    onResourceFetched={onResourceFetched}
                    isTargetSelected={isTargetSelected}
                />
            )}
        </div>
    );
};

// --- Main Component ---
const ReportScopeSelector = ({ name, inputTitle, details, required }: EditorInputProps) => {
    const { setValue, register, getValues } = useFormContext();
    const programId = useWatch({ name: "programId" });
    const runId = useWatch({ name: "runId" });
    const selectedAssetId = useWatch({ name: "assetId" });
    const selectedTargetIds = useWatch({ name: "targetIds" }) || [];
    const selectedEndpointIds = useWatch({ name: "endpointIds" }) || [];
    const isRunsLoading = useWatch({ name: "isRunsLoading" });

    useEffect(() => { register("assetId"); }, [register]);

    const { program, isLoading: isLoadingProgram } = useProgram(programId);
    const { data: snapshotOptions, isLoading: isLoadingSnapshot } = useGetRunScopeOptionsQuery(runId!, { skip: !runId });
    const useSnapshot = !!runId;

    // Track programId changes to prevent resetting scope on initial load or route transition
    const prevProgramIdRef = useRef<any>(programId);

    useEffect(() => {
        // If the program ID actually changed (manual selection by user)
        if (
            prevProgramIdRef.current !== undefined &&
            programId !== undefined &&
            programId !== null &&
            prevProgramIdRef.current !== programId
        ) {
            setValue("assetId", undefined);
            setValue("targetIds", []);
            setValue("endpointIds", []);
            setValue(name, "");
        }
        prevProgramIdRef.current = programId;
    }, [programId, setValue, name]);

    // Sync assetId once the program details or snapshot options load
    useEffect(() => {
        const assetId = program?.asset_id || program?.asset?.id || snapshotOptions?.asset?.id || snapshotOptions?.asset?.asset_id;
        if (assetId && (selectedAssetId === undefined || selectedAssetId === null || String(selectedAssetId) !== String(assetId))) {
            setValue("assetId", assetId);
        }
    }, [program, snapshotOptions, selectedAssetId, setValue]);

    const { data: liveTargets = [], isLoading: isLoadingTargets } = useGetAssetTargetsQuery(selectedAssetId, {
        skip: !selectedAssetId || useSnapshot
    });

    const targets = useSnapshot ? (snapshotOptions?.targets || []) : liveTargets;
    const activeTargets = useMemo(() => targets.filter((t: any) => {
        const s = String(t.status || "").toLowerCase();
        return s === 'active' || s === 'in_scope' || s === 'in scope';
    }), [targets]);

    // Multiple expansion state
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [isStep1Collapsed, setIsStep1Collapsed] = useState(false);
    const [targetSearch, setTargetSearch] = useState("");

    const isV2 = useMemo(() => (program as any)?.architecture_version >= 2, [program]);
    const v1Targets = useMemo(() => {
        if (isV2 || !program?.targets) return [];
        try {
            const raw = typeof program.targets === 'string' ? JSON.parse(program.targets) : program.targets;
            return Array.isArray(raw) ? raw : [];
        } catch (e) {
            return [];
        }
    }, [program, isV2]);

    // Handle initial selection for legacy reports
    useEffect(() => {
        if (!isV2 && programId) {
            const val = getValues(name) || "";
            // If it's in "Asset | Target" format, strip the prefix
            const targetOnly = val.includes(" | ") ? val.split(" | ").pop()?.trim() : val.trim();
            
            if (targetOnly && targetOnly !== val) {
                setValue(name, targetOnly);
            }
        }
    }, [isV2, programId, getValues, name, setValue]);

    // Cache to store discovered/fetched resources for names in the preview
    const [resourceCache, setResourceCache] = useState<Record<number, any>>({});
    const onResourceFetched = useCallback((res: any) => {
        setResourceCache(prev => {
            if (prev[res.resource_id]) return prev;
            return { ...prev, [res.resource_id]: res };
        });
    }, []);

    const allResources = useMemo(() => {
        const merged: Record<number, any> = { ...resourceCache };
        if (useSnapshot && snapshotOptions?.endpointsByTarget) {
            Object.values(snapshotOptions.endpointsByTarget).flat().forEach((r: any) => {
                merged[r.resource_id] = r;
            });
        }
        return Object.values(merged);
    }, [resourceCache, useSnapshot, snapshotOptions]);

    // Global filtering
    const filteredTargets = useMemo(() => {
        return activeTargets.filter((t: any) => 
            !targetSearch || 
            t.target_name.toLowerCase().includes(targetSearch.toLowerCase()) || 
            t.target_value.toLowerCase().includes(targetSearch.toLowerCase())
        );
    }, [activeTargets, targetSearch]);

    // Selection Handlers
    const toggleTarget = useCallback((id: string, _name: string) => {
        const current = getValues("targetIds") || [];
        const next = current.includes(id) ? current.filter((i: string) => i !== id) : [...current, id];
        setValue("targetIds", next);
    }, [getValues, setValue]);

    const toggleEndpoint = useCallback((id: number, targetId: string) => {
        const current = getValues("endpointIds") || [];
        const isSel = current.includes(id);
        const next = isSel ? current.filter((i: number) => i !== id) : [...current, id];
        setValue("endpointIds", next);

        if (!isSel) {
            const targetsSel = getValues("targetIds") || [];
            if (!targetsSel.includes(targetId)) setValue("targetIds", [...targetsSel, targetId]);
        }
    }, [getValues, setValue]);

    const selectAllTargets = () => {
        const allFilteredSelected = filteredTargets.every(t => selectedTargetIds.includes(t.target_id));
        if (allFilteredSelected) {
            setValue("targetIds", selectedTargetIds.filter((id: string) => !filteredTargets.some(t => t.target_id === id)));
        } else {
            setValue("targetIds", Array.from(new Set([...selectedTargetIds, ...filteredTargets.map(t => t.target_id)])));
        }
    };

    // This runs on ANY change to rebuild the legacy scope string
    useEffect(() => {
        if (!isV2) return;
        if (!selectedAssetId || (!selectedTargetIds.length && !selectedEndpointIds.length)) {
            setValue(name, "");
            return;
        }

        const assetName = program?.asset_name || program?.title || "Asset";
        const targetsStr = activeTargets
            .filter(t => selectedTargetIds.includes(t.target_id))
            .map(t => t.target_value)
            .join(", ") || "No Targets";

        const endpointsCount = selectedEndpointIds.length;
        const endpointsStr = endpointsCount > 0 ? ` (${endpointsCount} Endpoints)` : "";

        setValue(name, `${assetName} | ${targetsStr}${endpointsStr}`);
    }, [selectedAssetId, selectedTargetIds, selectedEndpointIds, program, activeTargets, setValue, name]);

    const isLoadingTargetsData = useSnapshot ? isLoadingSnapshot : isLoadingTargets;

    if (!programId) {
        return (
            <EditorInput {...{name, inputTitle, details, required}}>
                <div className="text-sm text-gray-500 italic py-2">
                    Please select a program first
                </div>
            </EditorInput>
        );
    }

    if (isLoadingProgram || isRunsLoading !== false || (isV2 && selectedAssetId && (isLoadingTargetsData || (useSnapshot && !snapshotOptions)))) {
        return <EditorInput {...{name, inputTitle, details, required}}> <div className="py-8 text-center animate-pulse text-slate-400">Loading...</div> </EditorInput>;
    }

    if (!isV2) {
        const currentValue = getValues(name) || "";
        return (
            <EditorInput {...{name, inputTitle, details, required}}>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase text-slate-400">Target Selection</span>
                    </div>

                    <div className="relative group">
                        <select
                            {...register(name)}
                            className="w-full pl-3 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-medium text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all appearance-none cursor-pointer"
                            onChange={(e) => setValue(name, e.target.value)}
                            value={currentValue}
                        >
                            <option value="">Select a target...</option>
                            {v1Targets.map((t: any, idx: number) => (
                                <option key={idx} value={t.targetName || t}>{t.targetName || t}</option>
                            ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Chevron className="w-4 h-4 text-slate-400" />
                        </div>
                    </div>
                    
                    {currentValue && (
                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
                             <MdCheck className="text-blue-600" />
                             <span className="text-[10px] font-bold text-blue-700 uppercase">Selected: {currentValue}</span>
                        </div>
                    )}
                </div>
            </EditorInput>
        );
    }
    return (
        <EditorInput {...{name, inputTitle, details, required}}>
            <div className="space-y-6">
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Step 1: Targets Selection</label>
                        <div className="flex items-center gap-2">
                            {filteredTargets.length > 0 && (
                                <div className="flex items-center bg-slate-100 rounded p-0.5">
                                    <button type="button" onClick={() => setExpandedIds(filteredTargets.map(t => t.target_id))} className="p-1 hover:text-blue-600" title="Expand All"><MdUnfoldMore className="w-3.5 h-3.5" /></button>
                                    <button type="button" onClick={() => setExpandedIds([])} className="p-1 hover:text-blue-600" title="Collapse All"><MdUnfoldLess className="w-3.5 h-3.5" /></button>
                                </div>
                            )}
                            <button type="button" onClick={selectAllTargets} className="text-[9px] font-bold text-blue-600 uppercase">
                                {filteredTargets.every(t => selectedTargetIds.includes(t.target_id)) ? "Deselect Filtered" : "Select Filtered"}
                            </button>
                            <button type="button" onClick={() => setIsStep1Collapsed(!isStep1Collapsed)} className="p-1 text-slate-400">
                                <Chevron className={`w-4 h-4 transition-transform ${isStep1Collapsed ? '' : 'rotate-180'}`} />
                            </button>
                        </div>
                    </div>

                    {!isStep1Collapsed && (
                        <>
                            <div className="relative">
                                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    placeholder="Search targets..." 
                                    value={targetSearch} 
                                    onChange={(e) => setTargetSearch(e.target.value)} 
                                    className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border rounded-lg text-[11px] outline-none focus:border-blue-400"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                                {filteredTargets.map(target => {
                                    const cacheCount = Object.values(resourceCache).filter((r: any) => r.target_id === target.target_id).length;
                                    const snapshotCount = (useSnapshot && snapshotOptions?.endpointsByTarget?.[target.target_id]?.length) || 0;
                                    const resourceCount = Math.max(target.resource_count || 0, cacheCount, snapshotCount);

                                    return (
                                        <TargetItem 
                                            key={target.target_id}
                                            target={target}
                                            selectedAssetId={selectedAssetId}
                                            useSnapshot={useSnapshot}
                                            snapshotOptions={snapshotOptions}
                                            programId={programId}
                                            selectedTargetIds={selectedTargetIds}
                                            selectedEndpointIds={selectedEndpointIds}
                                            toggleTarget={toggleTarget}
                                            isExpanded={expandedIds.includes(target.target_id)}
                                            onToggleExpansion={() => setExpandedIds(prev => prev.includes(target.target_id) ? prev.filter(id => id !== target.target_id) : [...prev, target.target_id])}
                                            toggleEndpoint={toggleEndpoint}
                                            onResourceFetched={onResourceFetched}
                                            resourceCount={resourceCount}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {(selectedTargetIds.length > 0 || selectedEndpointIds.length > 0) && (
                    <div className="p-4 bg-slate-900 rounded-xl text-white space-y-4 border border-slate-800 shadow-xl overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-4 opacity-5">
                            <MdCheck className="w-16 h-16" />
                        </div>
                        <div className="flex items-center justify-between relative z-10 border-b border-slate-800 pb-2">
                            <span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Selection Preview</span>
                            <button type="button" onClick={() => { setValue("targetIds", []); setValue("endpointIds", []); }} className="text-[10px] font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1 transition-colors">Clear All <MdClose /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
                            <div className="space-y-1.5">
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">Asset</span>
                                <div className="text-xs font-bold text-blue-400 truncate bg-blue-500/10 px-2 py-1.5 rounded">{program?.asset_name || program?.title || "Asset"}</div>
                            </div>
                            <div className="space-y-1.5">
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">Targets ({selectedTargetIds.length})</span>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto custom-scrollbar">
                                    {activeTargets.filter(t => selectedTargetIds.includes(t.target_id)).map(t => (
                                        <span key={t.target_id} className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700 truncate">{t.target_value}</span>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">Endpoints ({selectedEndpointIds.length})</span>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto custom-scrollbar">
                                    {allResources.filter((r: any) => selectedEndpointIds.includes(r.resource_id)).map((r: any) => (
                                        <span key={r.resource_id} className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/20 truncate">{r.value}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </EditorInput>
    );
};

export default ReportScopeSelector;
